import React from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment';

import Tag from 'components/common/tag';
import Pagination from 'components/common/pagination';
import Modal from 'components/common/modal';
import { ReactComponent as AddIcon } from 'assets/vectors/add-green-16px.svg';
import useEquipmentStatus from 'helpers/useEquipmentStatus';
import Filters from 'components/common/equipment/filters';
import { ReactComponent as OrderDownIcon } from 'assets/vectors/order-down-16px.svg';
import useExpirationColor from 'helpers/useExpirationColor';

const FendersQuery = gql`
  query Fenders ($first: Int, $after: String, $last: Int, $before: String, $sortBy: [String], $status: String, $diameter: Float, $length: Float, $location: String, $manufacturer: String, $pressure: String, $rope: String){
    fenders(first: $first, after: $after, last: $last, before: $before, sortBy: $sortBy, status: $status, diameter: $diameter, length: $length, location: $location, manufacturer: $manufacturer, pressure: $pressure, rope: $rope) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          id
          plNumber
          serialNumber
          diameter
          length
          location
          status
          expireDatePvValve
          lastProducts
        }
      }
    }
  }
`;

const AddFendersMutation = gql`
  mutation AddStsProjectEquipment($input: AddStsProjectEquipmentInput!) {
    addStsProjectEquipment(addStsProjectEquipmentData: $input) {
      success
    }
  }
`;

const resultCount = 10;

function FendersModal({ setShowFendersModal, projectId, refetch, addedFenders, onAdd }) {
  const [selectedFenders, setSelectedFenders] = React.useState([]);
  const [paginationVariables, setPaginationVariables] = React.useState({first: resultCount});
  const {tagColor, equipmentStatusLabel} = useEquipmentStatus();
  const [filters, setFilters] = React.useState([]);
  const [sortBy, setSortBy] = React.useState({ type: 'plNumber', direction: 'asc' });
  const {textColor} = useExpirationColor();

  const filterVariables = React.useMemo(() => {
    return filters.reduce((prev, cur) => {
      return {
        ...prev,
        [cur.key]: cur.val,
      }
    }, {});
  }, [filters]);

  const { data, loading: listLoading } = useQuery(FendersQuery, {
    variables: {
      ...paginationVariables,
      ...filterVariables,
      sortBy: [`${sortBy.type} ${sortBy.direction}`]
    },
    fetchPolicy: 'network-only',
  });

  const [addFenders, { loading: addLoading }] = useMutation(AddFendersMutation, {
    variables: {
      input: {
        stsprojectId: projectId,
        equipmentIds: selectedFenders,
        equipmentType: 'fender',
      },
    },
    onCompleted() {
      refetch();
      onAdd && onAdd()
      setShowFendersModal(false);
    },
    onError(e) {
      console.log(e)
    }
  });

  const fenders = data ? data.fenders.edges.map(u => u.node) : [];
  const pageInfo = data ? data.fenders.pageInfo : {};

  function checkboxHandler(fenderId) {
    if (selectedFenders.includes(fenderId)) {
      setSelectedFenders(selectedFenders.filter(id => id !== fenderId));
    } else {
      setSelectedFenders([...selectedFenders, fenderId]);
    }
  }

  function checkboxDisabled(fender) {
    return addedFenders.includes(fender.id) || fender.status !== 'stand_by';
  }

  function sortHandler(type) {
    if (type !== sortBy.type) {
      setSortBy({ type, direction: 'desc' });
    } else if (type === sortBy.type && sortBy.direction === 'desc') {
      setSortBy({ ...sortBy, direction: 'asc'});
    } else if (type === sortBy.type && sortBy.direction === 'asc') {
      setSortBy({ ...sortBy, direction: 'desc'});
    }
  }

  const addButtonDisabled = listLoading || addLoading || !selectedFenders.length

  return (
    <Modal onClose={() => setShowFendersModal(false)} title="FENDERS" style={{ width: '90%'}} preventOutsideClick>
      <Title>ADD FENDERS</Title>
      <Filters filters={filters} setFilters={setFilters} equipmentType="fender" />
      {
        listLoading
          ? <CircularProgress size={64} style={{ margin: '128px auto', display: 'block' }} />
          : (
            <>
              <TableContainer>
                <Table aria-label="simple table" style={{ whiteSpace: 'nowrap' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('plNumber')}>
                        PL Nr. <StyledOrderIcon active={sortBy.type === 'plNumber' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('serialNumber')}>
                        Serial Nr. <StyledOrderIcon active={sortBy.type === 'serialNumber' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('diameter')}>
                        Diameter <StyledOrderIcon active={sortBy.type === 'diameter' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('length')}>
                        Length <StyledOrderIcon active={sortBy.type === 'length' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('location')}>
                        Location <StyledOrderIcon active={sortBy.type === 'location' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('expireDatePvValve')}>
                        Expire date PV valve <StyledOrderIcon active={sortBy.type === 'expireDatePvValve' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell style={{ cursor: 'pointer'}} onClick={() => sortHandler('status')}>
                        Status <StyledOrderIcon active={sortBy.type === 'status' ? 1 : 0} direction={sortBy.direction} />
                      </TableCell>
                      <TableCell>
                        Last products
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {
                    !fenders.length
                      ? <TableRow>
                          <TableCell>No results</TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                        </TableRow>
                      : (
                        fenders.map(fender => (
                          <TableRow key={fender.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedFenders.includes(fender.id)}
                                onChange={() => checkboxHandler(fender.id)}
                                name="Now"
                                color="primary"
                                disabled={checkboxDisabled(fender)}
                              />
                            </TableCell>
                            <TableCell>
                              <Link target="_blank" href={`/dashboard/equipment/fenders/${fender.id}`}>{fender.plNumber}</Link>
                            </TableCell>
                            <TableCell>
                              {fender.serialNumber}
                            </TableCell>
                            <TableCell>
                              {fender.diameter}
                            </TableCell>
                            <TableCell>
                              {fender.length}
                            </TableCell>
                            <TableCell>
                              {fender.location}
                            </TableCell>
                            <TableCell style={{color: textColor(fender.expireDatePvValve)}}>
                              {fender.expireDatePvValve ? moment(fender.expireDatePvValve, 'YYYY-MM-DD').format('DD/MM/YYYY') : null}
                            </TableCell>
                            <TableCell>
                              <Tag color={tagColor(fender.status)} label={equipmentStatusLabel(fender.status)} />
                            </TableCell>
                            <TableCell>
                              {fender.lastProducts}
                            </TableCell>
                          </TableRow>
                        ))
                      )
                    }
                  </TableBody>
                </Table>
              </TableContainer>
              <Pagination
                paginationVariables={paginationVariables}
                setPaginationVariables={setPaginationVariables}
                resultCount={resultCount}
                pageInfo={pageInfo}
              />
              <ButtonWrapper>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={addFenders}
                  endIcon={<AddIcon />}
                  disabled={addButtonDisabled}
                >
                Add
                </Button>
              </ButtonWrapper>
            </>
          )
      }
    </Modal>
  );
}

const Link = styled.a`
  color: ${props => props.theme.colors.primaryColor};
  text-decoration: none;
`;

const Title = styled.div`
  color: ${props => props.theme.colors.primaryColor};
  font-weight: 700;
  margin-bottom: 36px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;

  @media (${props => props.theme.breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;

const StyledOrderIcon = styled(OrderDownIcon)`
  display: ${props => props.active ? 'inline-flex' : 'none'};
  transform: ${props => props.direction === 'desc' ? 'none' : 'rotate(180deg)'};
  margin-left: 4px;
  position: relative;
  top: 4px;
`;

export default FendersModal;
